import { CampaignStatusSchema, CampaignType } from "@adlywise/shared";
import { toDateInputValue } from "@/utils/dateUtils";

const Status = CampaignStatusSchema.enum;

export const STATUSES = [
  { value: Status.draft, label: "Draft" },
  { value: Status.active, label: "Active" },
  { value: Status.paused, label: "Paused" },
  { value: Status.completed, label: "Completed" },
];

export const DEFAULT_TRANSIT = {
  entrypoint: "",
  // Our commission (% of the publisher floor) before the 50/50 profit split.
  margin: 0,
  qps: 1,
  // Blank ⇒ the platform default response budget is used.
  timeoutMs: "",
  // Routing filters for this DSP endpoint (see EndpointFilters); null ⇒ none.
  filters: null,
  selectedPublishers: [],
  blockList: [],
  whiteList: [],
  // Server-minted public statistics token; read-only here, carried through a
  // save so editing a campaign never drops its partner link.
  statsToken: "",
};

export const DEFAULT_CAMPAIGN = {
  name: "",
  description: "",
  status: Status.draft,
  type: CampaignType.Standard,
  selectedSegments: [],
  userId: "",
  defaultCreativeId: "",
  startDate: "",
  endDate: "",
  icon: null,
  cpm: 0,
  dailyFrequency: "",
  weeklyFrequency: "",
  impressionsCap: "",
  budgetCap: "",
  cooldownSeconds: "",
  // creativeUuid -> cooldown seconds (kept as input-friendly strings in the form)
  creativeCooldowns: {},
  impressionDomain: null,
  metadata: { ...DEFAULT_TRANSIT },
};

export const toFormState = (campaign) => {
  if (!campaign)
    return { ...DEFAULT_CAMPAIGN, metadata: { ...DEFAULT_TRANSIT } };

  // Read metadata for both types, not transit-only: standard campaigns now also
  // persist `selectedPublishers` here, so editing must hydrate it to survive a
  // round-trip.
  const rawMeta =
    campaign.metadata && typeof campaign.metadata === "object"
      ? campaign.metadata
      : {};
  const isTransit = campaign.type === CampaignType.Transit;
  return {
    ...DEFAULT_CAMPAIGN,
    ...campaign,
    type: isTransit ? CampaignType.Transit : CampaignType.Standard,
    selectedSegments: Array.isArray(campaign.selectedSegments)
      ? campaign.selectedSegments
          .map(String)
          .filter((id) => id && id !== "undefined" && id !== "null")
      : [],
    defaultCreativeId: campaign.defaultCreativeId ?? "",
    startDate: toDateInputValue(campaign.startDate),
    endDate: toDateInputValue(campaign.endDate),
    cpm: campaign.cpm ?? 0,
    dailyFrequency:
      campaign.dailyFrequency != null ? String(campaign.dailyFrequency) : "",
    weeklyFrequency:
      campaign.weeklyFrequency != null ? String(campaign.weeklyFrequency) : "",
    impressionsCap:
      campaign.impressionsCap != null ? String(campaign.impressionsCap) : "",
    budgetCap: campaign.budgetCap != null ? String(campaign.budgetCap) : "",
    cooldownSeconds:
      campaign.cooldownSeconds != null ? String(campaign.cooldownSeconds) : "",
    creativeCooldowns:
      campaign.creativeCooldowns &&
      typeof campaign.creativeCooldowns === "object"
        ? Object.fromEntries(
            Object.entries(campaign.creativeCooldowns).map(([id, secs]) => [
              id,
              String(secs),
            ]),
          )
        : {},
    impressionDomain: campaign.impressionDomain ?? null,
    metadata: {
      entrypoint: isTransit ? (rawMeta.entrypoint ?? "") : "",
      margin: isTransit ? (rawMeta.margin ?? 0) : 0,
      qps: isTransit ? (rawMeta.qps ?? 1) : 1,
      timeoutMs:
        isTransit && rawMeta.timeoutMs != null ? String(rawMeta.timeoutMs) : "",
      filters: isTransit ? (rawMeta.filters ?? null) : null,
      statsToken: isTransit ? (rawMeta.statsToken ?? "") : "",
      selectedPublishers: Array.isArray(rawMeta.selectedPublishers)
        ? rawMeta.selectedPublishers
        : [],
      blockList: isTransit
        ? Array.isArray(rawMeta.blockList)
          ? rawMeta.blockList
          : []
        : [],
      whiteList: isTransit
        ? Array.isArray(rawMeta.whiteList)
          ? rawMeta.whiteList
          : []
        : [],
    },
  };
};

export const toNullableInt = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
};

export const toNullableFloat = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// Build the API `creativeCooldowns` map: keep only positive-integer seconds and
// only for creatives currently assigned to the campaign (drops stale entries for
// creatives that were unassigned). Returns {} when nothing is set.
export const round2 = (n) =>
  Math.round((Number(n) + Number.EPSILON) * 100) / 100;

export const maxProviderCost = (selectedPublishers, providers) => {
  const byUuid = new Map(
    (Array.isArray(providers) ? providers : []).map((p) => [
      p.uuid,
      Number(p.cpm) || 0,
    ]),
  );
  return (Array.isArray(selectedPublishers) ? selectedPublishers : []).reduce(
    (max, uuid) => Math.max(max, byUuid.get(uuid) ?? 0),
    0,
  );
};

// Minimum allowed campaign CPM for a standard campaign: strictly above the
// priciest selected provider's cost (serving requires campaign cpm > provider
// cost). Returns 0 when no providers are selected.
export const minCampaignCpm = (selectedPublishers, providers) => {
  if (!(Array.isArray(selectedPublishers) && selectedPublishers.length > 0))
    return 0;
  return round2(maxProviderCost(selectedPublishers, providers) + 0.01);
};

export const toCreativeCooldownsPayload = (map, assignedIds) => {
  const allowed = new Set((assignedIds ?? []).filter(Boolean));
  const out = {};
  for (const [id, value] of Object.entries(map ?? {})) {
    if (!allowed.has(id)) continue;
    const n = parseInt(value, 10);
    if (Number.isFinite(n) && n > 0) out[id] = n;
  }
  return out;
};
